// React imports
import React from 'react';
import { useForm } from 'react-hook-form';

// Material-UI imports

// Other imports
import { useLazyQuery, useMutation } from '@apollo/client';
import qs from 'qs';
// @ts-ignore
import { yupResolver } from '@hookform/resolvers/yup';
// @ts-ignore
import * as yup from 'yup';

// Absolute imports
import { GET_LOGIN_META } from 'modules/user/queries';
import { LOGIN, PASSWORD_CHANGE } from 'modules/user/mutations';
import {
  ErrorsEnumLogin,
  ErrorsEnumPasswordChange,
} from 'libs/api/invest/generated/schema';

// Relative imports

enum AUTH_TYPES {
  PASSWORD,
  PASSWORD_CHANGE,
  SSO,
  PLATFORM_OIDC,
}

type FormDataLogin = {
  email: string;
  password: string;
};

type FormDataPasswordChange = {
  password1: string;
  password2: string;
};

const schemaLogin = yup.object().shape({
  email: yup
    .string()
    .email('Please enter a valid email address.')
    .required('Please enter an email'),
  password: yup.string().required('Please enter a password.'),
});

const schemaPasswordChange = yup.object().shape({
  password1: yup.string().required('Please enter new password.'),
  password2: yup.string().required('Please re-enter new password.'),
});

export function useLoginPage(query: string) {
  //Refs

  // States
  const [loginInProgress, setLoginInProgress] = React.useState<boolean>(false);
  const [showPassword, setShowPassword] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string|string[]|null>(null);
  const [authType, setAuthType] = React.useState<null | AUTH_TYPES>(null);

  // Hooks
  const formLogin = useForm<FormDataLogin>({
    mode: 'onBlur',
    defaultValues: {
      email: '',
      password: '',
    },
    //@ts-ignore
    resolver: yupResolver(schemaLogin),
  });
  const formPasswordChange = useForm<FormDataPasswordChange>({
    mode: 'onBlur',
    defaultValues: {
      password1: '',
      password2: '',
    },
    //@ts-ignore
    resolver: yupResolver(schemaPasswordChange),
  });
  const { email, password } = formLogin.watch();
  const { password1, password2 } = formPasswordChange.watch();
  const [loginMeta, { data, loading: loginMetaLoading }] = useLazyQuery(GET_LOGIN_META);
  const [login] = useMutation(LOGIN);
  const [passwordChange] = useMutation(PASSWORD_CHANGE);

  // Effects
  React.useEffect(() => {
    setAuthType(null);
    if (email.match(/^.+@.+\..*[^.]$/)) {
      loginMeta({ variables: { email } });
    }
  }, [email, loginMeta]);

  React.useEffect(() => {
    setErrorMessage(null);
  }, [email, password, password1, password2]);

  React.useEffect(() => {
    const loginMeta = data?.loginMeta;

    if (loginMeta) {
      setAuthType(AUTH_TYPES.PASSWORD);

      if (
        loginMeta.nonPlatformLoginExists === true ||
        loginMeta.platformLoginExists === true
      ) {
        if (loginMeta.nonPlatformLoginExists === true) {
          if (loginMeta.ssoLoginUrl) {
            setAuthType(AUTH_TYPES.SSO);
          }
        } else if (loginMeta.platformLoginExists === true) {
          if (loginMeta.oidcLoginUrl) {
            setAuthType(AUTH_TYPES.PLATFORM_OIDC);
          }
        }
      }
    }
  }, [data]);

  React.useEffect(() => {
    const queryParams = qs.parse(query, { ignoreQueryPrefix: true });
    const errorMessages: { [key: string]: string } = {
      sso_disabled: 'SSO is disabled for this user',
      sso_failed: 'SSO process failed',
      user_not_found:
        'User not found. Check if your SSO account email is correct',
    };
    setErrorMessage(errorMessages[queryParams.err as string]);
  }, [query]);

  // Body code

  // Functions
  function getOnSubmitLogin() {
    return formLogin.handleSubmit(async (data: FormDataLogin) => {

      setLoginInProgress(true);
      await login({ variables: { email: data.email, password: data.password } })
        .then(res => {
    
          const { session, error } = res.data.login;
          if (session) {
            loginRedirect();
          } else {

            setLoginInProgress(false);
            if (error === ErrorsEnumLogin.PASSWORD_EXPIRED) {
              setAuthType(AUTH_TYPES.PASSWORD_CHANGE);
            } else if (error === ErrorsEnumLogin.INACTIVE) {
              setErrorMessage('This account is disabled.');
            } else if (error === ErrorsEnumLogin.WRONG_CREDS) {
              setErrorMessage('Incorrect email or password.');
            } else if (error === ErrorsEnumLogin.RATE_LIMITED) {
              setErrorMessage('Too many login attempts. Please try again later.');
            } else {
              setErrorMessage('Something went wrong.');
              console.warn(`Unknown error: ${error}`)
            }
          }
        }).catch(error => {
          if (error.networkError?.response?.status === 403) {
            // will be handled by server || CSRF error
            window.location.reload()
          }
        })
    });
  }

  function getOnSubmitPasswordChange() {
    return formPasswordChange.handleSubmit(async (data: FormDataPasswordChange) => {
      setLoginInProgress(true);
      await passwordChange({ variables: { email, oldPassword: password, password1: data.password1, password2: data.password2 } })
        .then(res => {
          const { session, errorCode, additionalMessages } = res.data.passwordChange;
          if (session) {
            loginRedirect();
          } else {
            setLoginInProgress(false);
            if (errorCode === ErrorsEnumPasswordChange.INACTIVE) {
              setErrorMessage('This account is disabled.');
            } else if (errorCode === ErrorsEnumPasswordChange.WRONG_CREDS || errorCode === ErrorsEnumPasswordChange.UNABLE_TO_AUTH) {
              setErrorMessage('Incorrect email or password.');
            } else if (errorCode === ErrorsEnumPasswordChange.PASSWORDS_MISMATCH) {
              setErrorMessage('Passwords do not match.');
            } else if (errorCode === ErrorsEnumPasswordChange.SAME_PASSWORD) {
              setErrorMessage('New password can not be same as old one.');
            } else if (errorCode === ErrorsEnumPasswordChange.INVALID_PASSWORD) {
              setErrorMessage(
                Array.isArray(additionalMessages) && additionalMessages.length
                  ? additionalMessages.length === 1 ? additionalMessages[0] : additionalMessages
                  : 'New password does not match requirements.'
              );
            } else {
              setErrorMessage('Something went wrong.');
              console.warn(`Unknown error: ${errorCode}`)
            }
          }
        });
    });
  }

  /**
   * Given an input string, this checks if it contains a domain.
   * Ref: https://rubular.com/r/KByADagF3Z
   */
  const isDomain = (input: string) => {
    const regex = /[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+/
    try {
      return regex.test(input)
    } catch (e) {
      return false
    }
  }

  /**
   * Given an input string, this returns if it is a `valid` and `safe` redirect 
   * url/path. This is implemented to prevent XSS attacks during login.
   * 
   * Note that redirects only happen within same invest app.
   * 
   * Logic:
   *  1. Remove unnecessary characters like: `//`.
   *  2. Make sure no doamin is present in string (outside domain redirect).
   *  3. Check if input was valid by comparing the lenght of string. 
   */
  const isValidRedirectPath = (path: string) => {
    // input is invalid | skip
    if (!path.startsWith('/')) {
      return false
    }

    // pre-processing to prevent `//`
    if (path.endsWith('/')) {
      path = path.slice(0, -1);
    }
    const processedPath = '/' + path.split('/').filter(s => s.trim() !== '' && !isDomain(s)).join('/')
    
    // input string should be of same lenght after processing
    return processedPath.length === path.length
  }

  function loginRedirect() {
    const next = new URLSearchParams(window.location.search).get('next');
    if (next?.startsWith('/storage/')) {
      window.location.href = `/?download=${next}`;
    }
    else {
      window.location.href = next && isValidRedirectPath(next) ? next : '/';
    }
  }

  function loginSso() {
    if (data?.loginMeta.ssoLoginUrl) window.location.href = data.loginMeta.ssoLoginUrl;
  }

  function loginPlatformOidc() {
    if (data?.loginMeta.oidcLoginUrl) window.location.href = data.loginMeta.oidcLoginUrl;
  }

  function toggleShowPassword() {
    setShowPassword(!showPassword);
  }

  return {
    AUTH_TYPES,
    authType,
    errorMessage,
    formLogin,
    formPasswordChange,
    getOnSubmitLogin,
    getOnSubmitPasswordChange,
    loginInProgress,
    loginSso,
    loginPlatformOidc,
    loginMetaLoading,
    showPassword,
    toggleShowPassword,
  };
}